import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";

import PublicLayout from "./components/PublicLayout";
import DashboardShell from "./components/DashboardShell";
import ProtectedRoute from "./components/ProtectedRoute";
import usePageTracker from "./lib/usePageTracker";
import { useDocumentSeo } from "./lib/useDocumentSeo";
import { PopupHost } from "./components/PopupManager";

// Keep the light landing pages eagerly loaded so first paint is instant.
import Home from "./pages/Home";
import Login from "./pages/Login";

// Lazy-load everything else so admin pages don't bloat the public bundle.
const Products = lazy(() => import("./pages/Products"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Delivery = lazy(() => import("./pages/Delivery"));
const Signup = lazy(() => import("./pages/Signup"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const DriverApply = lazy(() => import("./pages/DriverApply"));
const DriverPending = lazy(() => import("./pages/DriverPending"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const TrackOrder = lazy(() => import("./pages/TrackOrder"));

const CustomerDashboard = lazy(() => import("./pages/customer/CustomerDashboard"));
const OrderHistory = lazy(() => import("./pages/customer/OrderHistory"));
const Profile = lazy(() => import("./pages/customer/Profile"));

const DriverDashboard = lazy(() => import("./pages/driver/DriverDashboard"));
const DeliveryDetails = lazy(() => import("./pages/driver/DeliveryDetails"));

const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const OrdersManagement = lazy(() => import("./pages/admin/OrdersManagement"));
const ProductsManagement = lazy(() => import("./pages/admin/ProductsManagement"));
const CategoriesManagement = lazy(() => import("./pages/admin/CategoriesManagement"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const DriversList = lazy(() => import("./pages/admin/DriversList"));
const LiveDrivers = lazy(() => import("./pages/admin/LiveDrivers"));
const CustomersList = lazy(() => import("./pages/admin/CustomersList"));
const DriverApprovals = lazy(() => import("./pages/admin/DriverApprovals"));
const AdminNotifications = lazy(() => import("./pages/admin/AdminNotifications"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminWholesale = lazy(() => import("./pages/admin/AdminWholesale"));
const AdminCallCenter = lazy(() => import("./pages/admin/AdminCallCenter"));
const AdminCMS = lazy(() => import("./pages/admin/AdminCMS"));
const AdminEmailOutbox = lazy(() => import("./pages/admin/AdminEmailOutbox"));
const AdminBulkEmail = lazy(() => import("./pages/admin/AdminBulkEmail"));
const AdminDeliverySlots = lazy(() => import("./pages/admin/AdminDeliverySlots"));
const AdminNotifyRequests = lazy(() => import("./pages/admin/AdminNotifyRequests"));
const AdminProductRequests = lazy(() => import("./pages/admin/AdminProductRequests"));
const AdminWholesalePricing = lazy(() => import("./pages/admin/AdminWholesalePricing"));
const AdminAuditLog = lazy(() => import("./pages/admin/AdminAuditLog"));
const AdminPageBuilder = lazy(() => import("./pages/admin/AdminPageBuilder"));
const AdminPageEditor = lazy(() => import("./pages/admin/AdminPageEditor"));
const CustomPage = lazy(() => import("./pages/CustomPage"));
const Suggest = lazy(() => import("./pages/Suggest"));
const MagicLinkCallback = lazy(() => import("./pages/MagicLinkCallback"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));

const WholesaleLanding = lazy(() => import("./pages/wholesale/WholesaleLanding"));
const WholesaleApply = lazy(() => import("./pages/wholesale/WholesaleApply"));
const WholesaleLogin = lazy(() => import("./pages/wholesale/WholesaleLogin"));
const WholesaleDashboard = lazy(() => import("./pages/wholesale/WholesalePages").then((m) => ({ default: m.WholesaleDashboard })));
const WholesaleProducts = lazy(() => import("./pages/wholesale/WholesalePages").then((m) => ({ default: m.WholesaleProducts })));
const WholesaleOrders = lazy(() => import("./pages/wholesale/WholesalePages").then((m) => ({ default: m.WholesaleOrders })));

import { LayoutDashboard, ClipboardList, User as UserIcon, Truck, Package, Users, ShoppingBag, ShieldCheck, Bell, Tags, Settings as SettingsIcon, MapPin, BarChart3, Building2, PhoneCall, FileText, Mail, Layout, Send, Lightbulb, Calendar, DollarSign, ScrollText } from "lucide-react";

const customerNav = [
  { to: "/customer/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/customer/orders", label: "Order History", icon: ClipboardList },
  { to: "/customer/profile", label: "Profile", icon: UserIcon },
  { to: "/cart", label: "Cart", icon: ShoppingBag },
];

const driverNav = [
  { to: "/driver/dashboard", label: "My Deliveries", icon: Truck, end: true },
];

const adminNav = [
  { to: "/admin/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/cms", label: "Content Studio", icon: FileText, highlight: true },
  { to: "/admin/builder", label: "Page Builder", icon: Layout },
  { to: "/admin/email-outbox", label: "Email Outbox", icon: Mail },
  { to: "/admin/bulk-emails", label: "Bulk Email", icon: Send, highlight: true },
  { to: "/admin/analytics", label: "Traffic", icon: BarChart3 },
  { to: "/admin/orders", label: "Orders", icon: ClipboardList },
  { to: "/admin/delivery-slots", label: "Delivery Slots", icon: Calendar },
  { to: "/admin/calls", label: "Call Center", icon: PhoneCall, highlight: true },
  { to: "/admin/live", label: "Live Drivers", icon: MapPin },
  { to: "/admin/notifications", label: "Notifications", icon: Bell },
  { to: "/admin/approvals", label: "Driver Approvals", icon: ShieldCheck },
  { to: "/admin/wholesale", label: "Wholesale", icon: Building2 },
  { to: "/admin/wholesale-pricing", label: "Wholesale Pricing", icon: DollarSign },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/notify", label: "Notify-Me Waitlist", icon: Bell },
  { to: "/admin/product-requests", label: "Product Suggestions", icon: Lightbulb, highlight: true },
  { to: "/admin/categories", label: "Categories", icon: Tags },
  { to: "/admin/drivers", label: "Drivers", icon: Truck },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/audit-log", label: "Audit Log", icon: ScrollText },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
];

const wholesaleNav = [
  { to: "/wholesale/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/wholesale/products", label: "Catalogue", icon: Package },
  { to: "/wholesale/orders", label: "Order History", icon: ClipboardList },
];

function TrackerInner() {
  usePageTracker();
  useDocumentSeo();
  return null;
}

function App() {
  return (
    <div className="App font-body">
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
              <TrackerInner />
              <PopupHost />
              <Toaster position="top-right" richColors />
              <Suspense fallback={
                <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
                  <div className="flex items-center gap-3 text-stone-500">
                    <div className="h-4 w-4 rounded-full border-2 border-[#EA580C] border-t-transparent animate-spin" />
                    <span className="text-sm">Loading…</span>
                  </div>
                </div>
              }>
              <Routes>
                <Route element={<PublicLayout />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/delivery" element={<Delivery />} />
                  <Route path="/wholesale" element={<WholesaleLanding />} />
                  <Route path="/wholesale/apply" element={<WholesaleApply />} />
                  <Route path="/wholesale/login" element={<WholesaleLogin />} />
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/checkout" element={<ProtectedRoute role="customer"><Checkout /></ProtectedRoute>} />
                  <Route path="/track/:orderId" element={<TrackOrder />} />
                  <Route path="/p/:slug" element={<CustomPage />} />
                  <Route path="/suggest" element={<Suggest />} />
                <Route path="/auth/magic" element={<MagicLinkCallback />} />
                </Route>

                <Route path="/login" element={<Login role="customer" />} />
                <Route path="/driver/login" element={<Login role="driver" />} />
                <Route path="/admin/login" element={<Login role="admin" />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/driver/apply" element={<DriverApply />} />
                <Route path="/driver/pending" element={<ProtectedRoute role="driver"><DriverPending /></ProtectedRoute>} />

                <Route element={<ProtectedRoute role="customer"><DashboardShell navItems={customerNav} accentLabel="Customer" /></ProtectedRoute>}>
                  <Route path="/customer/dashboard" element={<CustomerDashboard />} />
                  <Route path="/customer/orders" element={<OrderHistory />} />
                  <Route path="/customer/profile" element={<Profile />} />
                </Route>

                <Route element={<ProtectedRoute role="driver"><DashboardShell navItems={driverNav} accentLabel="Driver" /></ProtectedRoute>}>
                  <Route path="/driver/dashboard" element={<DriverDashboard />} />
                  <Route path="/driver/delivery/:id" element={<DeliveryDetails />} />
                </Route>

                <Route element={<ProtectedRoute role="wholesale"><DashboardShell navItems={wholesaleNav} accentLabel="Wholesale" /></ProtectedRoute>}>
                  <Route path="/wholesale/dashboard" element={<WholesaleDashboard />} />
                  <Route path="/wholesale/products" element={<WholesaleProducts />} />
                  <Route path="/wholesale/orders" element={<WholesaleOrders />} />
                </Route>

                <Route element={<ProtectedRoute role="admin"><DashboardShell navItems={adminNav} accentLabel="Admin" /></ProtectedRoute>}>
                  <Route path="/admin/dashboard" element={<AdminDashboard />} />
                  <Route path="/admin/cms" element={<AdminCMS />} />
                  <Route path="/admin/builder" element={<AdminPageBuilder />} />
                  <Route path="/admin/builder/:slug" element={<AdminPageEditor />} />
                  <Route path="/admin/email-outbox" element={<AdminEmailOutbox />} />
                  <Route path="/admin/bulk-emails" element={<AdminBulkEmail />} />
                  <Route path="/admin/analytics" element={<AdminAnalytics />} />
                  <Route path="/admin/orders" element={<OrdersManagement />} />
                  <Route path="/admin/delivery-slots" element={<AdminDeliverySlots />} />
                  <Route path="/admin/calls" element={<AdminCallCenter />} />
                  <Route path="/admin/live" element={<LiveDrivers />} />
                  <Route path="/admin/notifications" element={<AdminNotifications />} />
                  <Route path="/admin/approvals" element={<DriverApprovals />} />
                  <Route path="/admin/wholesale" element={<AdminWholesale />} />
                  <Route path="/admin/wholesale-pricing" element={<AdminWholesalePricing />} />
                  <Route path="/admin/products" element={<ProductsManagement />} />
                  <Route path="/admin/notify" element={<AdminNotifyRequests />} />
                  <Route path="/admin/product-requests" element={<AdminProductRequests />} />
                  <Route path="/admin/categories" element={<CategoriesManagement />} />
                  <Route path="/admin/drivers" element={<DriversList />} />
                  <Route path="/admin/customers" element={<CustomersList />} />
                  <Route path="/admin/audit-log" element={<AdminAuditLog />} />
                  <Route path="/admin/settings" element={<AdminSettings />} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </Suspense>
            </BrowserRouter>
          </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
